import { Button, Modal, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useState } from 'react';
import type { PhotoExportPart } from '@citizen-walk/shared-types';
import { useMyIdentity } from '../auth';
import { useDistrict } from '../district';
import { useExportMyPhotos } from './useExportMyPhotos';
import { savePhotoToDevice, useMyPhotoDownloads, type MyPhotoItem } from './useMyPhotoDownloads';
import type { PostMinimal } from './usePosts';

/**
 * 自分の投稿写真ダウンロード modal (Feature)。
 *
 * 地区設定 `photoConfig.photoDownloadMode` で 2 方式を切替える (2026-06-13):
 *   - `'zip'` (既定): サーバ非同期ジョブで分割 ZIP を生成 → DL リンク表示 (ZipDownloadBody、Pattern C / issue #28)。
 *   - `'individual'`: クライアントが自分の写真を列挙し、1 枚ずつ「保存」(IndividualDownloadBody)。
 *     ZIP を解凍できないスマホ参加者が多い地区向け。
 *
 * ZIP 方式: ボタンでジョブ登録 → 進捗バー + 完成した分割 ZIP (part-1, part-2, …) の DL リンク。
 * 途中で閉じても生成は継続し、開き直すと再開 (購読を貼り直し) する。
 */
export interface PhotoDownloadModalProps {
  open: boolean;
  onClose: () => void;
  districtId: string;
  /** サインイン中ユーザーの uid。null のときは生成不可 (サインイン待ち表示)。 */
  uid: string | null;
  /**
   * 個別ダウンロードモード用に、アプリが読み込み済みの投稿 (ルール準拠) を渡す。
   * モーダル内で自分 (引き継ぎ元含む) の写真付き投稿だけ抽出する。ZIP モードでは未使用。
   */
  posts?: readonly PostMinimal[];
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

/** 完成した分割 ZIP (パート) ごとに copy / download リンクを並べる。 */
function PartsList({ parts, onCopy }: { parts: PhotoExportPart[]; onCopy: (url: string) => void }) {
  if (parts.length === 0) return null;
  const multi = parts.length > 1;
  return (
    <div className="grid gap-2">
      {parts.map((p) => (
        <div key={p.index} className="grid gap-1 rounded border border-slate-200 p-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>
              {multi ? `パート ${p.index + 1}` : 'ダウンロード'} ({p.photoCount} 枚、
              {formatBytes(p.bytes)})
            </span>
          </div>
          <input
            type="text"
            readOnly
            value={p.downloadUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => onCopy(p.downloadUrl)}>
              📋 リンクをコピー
            </Button>
            <a href={p.downloadUrl} target="_blank" rel="noopener noreferrer">
              <Button type="button" variant="secondary">
                ⬇ ダウンロード
              </Button>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

/** ZIP まとめダウンロード本体 (従来方式、Pattern C 非同期ジョブ)。 */
function ZipDownloadBody({
  districtId,
  uid,
  open,
}: {
  districtId: string;
  uid: string | null;
  open: boolean;
}) {
  const { toast } = useToast();
  const { state, call, attach, reset } = useExportMyPhotos();

  // modal を開いたら進行中 / 完了ジョブがあれば購読を再開、閉じたら購読解除して idle に戻す。
  useEffect(() => {
    if (open) attach(districtId);
    else reset();
    // attach / reset は useCallback 安定。open / districtId 変化時のみ実行。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, districtId]);

  async function handleGenerate() {
    // enqueuing 中のみ二重起動を防ぐ。running 中でも「作り直す」で新規ジョブに切替できる
    // (ワーカー異常等で進捗が止まった場合の脱出手段)。
    if (!uid || state.status === 'enqueuing') return;
    const r = await call({ districtId });
    if (r) {
      toast(
        'info',
        r.totalPhotos > 0
          ? `写真 ${r.totalPhotos} 枚のまとめ作成を開始しました`
          : 'まとめ作成を開始しました',
      );
    }
  }

  async function handleCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast('info', 'リンクをコピーしました');
    } catch {
      toast('error', 'コピーに失敗しました (リンクを長押しでコピーしてください)');
    }
  }

  const showGenerate = state.status !== 'running' && state.status !== 'enqueuing';
  const onCopy = (url: string) => void handleCopy(url);

  return (
    <div className="grid gap-3 text-sm text-slate-700">
      <p>
        この地区であなたが投稿した写真を ZIP ファイルにまとめます。写真と投稿の対応表 (manifest.csv)
        も同梱されます。写真が多い場合は複数の ZIP に分けて作成します。
      </p>

      {!uid && (
        <p className="text-warning-700">
          サインインの準備中です。少し待ってから再度お試しください。
        </p>
      )}

      {showGenerate && (
        <Button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={!uid}
          className="w-full"
        >
          {state.status === 'done' || state.status === 'error' ? '🔄 作り直す' : 'ZIP を作成'}
        </Button>
      )}

      {state.status === 'enqueuing' && <p className="text-xs text-slate-500">準備しています…</p>}

      {state.status === 'running' && (
        <div className="grid gap-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>写真をまとめています…</span>
            <span>
              {state.processedPhotos} / {state.totalPhotos} 枚
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
            <div
              className="h-full bg-primary-500 transition-all"
              style={{
                width: `${
                  state.totalPhotos > 0
                    ? Math.min(100, Math.round((state.processedPhotos / state.totalPhotos) * 100))
                    : 0
                }%`,
              }}
            />
          </div>
          <p className="text-xs text-slate-500">
            この画面を閉じても作成は続きます。あとで開き直すと続きから確認できます。
          </p>
          {/* 完成したパートは順次ダウンロード可能。 */}
          <PartsList parts={state.parts} onCopy={onCopy} />
          {/* 進捗が長く止まったときの脱出手段 (ワーカー異常等)。新規ジョブに切替える。 */}
          <button
            type="button"
            onClick={() => void handleGenerate()}
            className="justify-self-start text-xs text-slate-400 underline"
          >
            進まない場合は作り直す
          </button>
        </div>
      )}

      {state.status === 'error' && (
        <p className="text-danger-700 text-sm">作成に失敗しました: {state.error.message}</p>
      )}

      {state.status === 'done' && (
        <div className="grid gap-2 border-t border-slate-200 pt-3">
          {state.parts.length === 0 ? (
            <p className="text-warning-700 text-sm">
              ダウンロードできる写真を取得できませんでした。時間をおいて作り直してください。
            </p>
          ) : (
            <>
              <p className="text-slate-700">
                写真 <strong>{state.totalPhotos}</strong> 枚を
                {state.parts.length > 1 ? ` ${state.parts.length} 個の ZIP に分けて` : ' '}
                まとめました。
              </p>
              {state.truncated && (
                <p className="text-xs text-warning-700">
                  ※ 枚数が上限に達したため、新しい順に一部の写真のみ含まれています。
                </p>
              )}
              <PartsList parts={state.parts} onCopy={onCopy} />
              <p className="text-[11px] text-slate-400">
                ダウンロードリンクの有効期限は約 3 日間です。期限が切れたら「作り直す」で再生成して
                ください。
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** 1 枚ずつ個別ダウンロード本体 (写真ごとに「保存」、ZIP 解凍不要)。 */
function IndividualDownloadBody({
  districtId,
  uid,
  open,
  posts,
}: {
  districtId: string;
  uid: string | null;
  open: boolean;
  posts: readonly PostMinimal[];
}) {
  const { toast } = useToast();
  const { allUids } = useMyIdentity(uid, districtId);
  const { state, reload } = useMyPhotoDownloads(posts, allUids, open);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  async function handleSave(item: MyPhotoItem) {
    setSavingId(item.postId);
    try {
      await savePhotoToDevice(item.downloadUrl, item.filename);
      setSavedIds((s) => {
        const next = new Set(s);
        next.add(item.postId);
        return next;
      });
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-3 text-sm text-slate-700 min-w-0">
      <p>
        この地区であなたが投稿した写真を、1
        枚ずつ端末に保存できます。各写真の「保存」を押してください。
      </p>

      {!uid && (
        <p className="text-warning-700">
          サインインの準備中です。少し待ってから再度お試しください。
        </p>
      )}

      {state.status === 'loading' && <p className="text-xs text-slate-500">写真を探しています…</p>}

      {state.status === 'error' && (
        <p className="text-danger-700 text-sm">
          写真の取得に失敗しました: {state.error.message}
          <button type="button" onClick={reload} className="ml-2 underline">
            再試行
          </button>
        </p>
      )}

      {state.status === 'ready' && state.photos.length === 0 && (
        <p className="text-warning-700">ダウンロードできる写真付きの投稿がありません。</p>
      )}

      {state.status === 'ready' && state.photos.length > 0 && (
        <>
          <p className="text-xs text-slate-500">写真 {state.photos.length} 枚</p>
          {/* 横オーバーフロー防止: grid は grid item に min-width:auto を与え横スクロールの原因に
              なるため flex flex-col を使い、各行は w-full・内部はサムネ/ボタンを shrink-0、
              タイトルを min-w-0 + truncate で縮ませる。overflow-x-hidden で横スクロールを無効化。 */}
          <ul className="flex flex-col gap-2 w-full max-h-[60vh] overflow-y-auto overflow-x-hidden">
            {state.photos.map((item) => (
              <li
                key={item.postId}
                className="flex w-full min-w-0 items-center gap-2 rounded border border-slate-200 p-2"
              >
                <img
                  src={item.thumbUrl}
                  alt=""
                  loading="lazy"
                  className="h-12 w-12 shrink-0 rounded border border-slate-200 object-cover"
                />
                <span className="min-w-0 flex-1 text-xs text-slate-600">
                  <span className="block truncate">{item.title || '(無題)'}</span>
                  {/* docs/28 §5-4: ぼかし前の写真か、ぼかし済み (ぼかし前の写真が無い) かを示す。 */}
                  <span className="block text-[11px] text-slate-400">
                    {item.original ? 'ぼかし前の写真' : 'ぼかし済みの写真'}
                  </span>
                </span>
                <Button
                  type="button"
                  onClick={() => void handleSave(item)}
                  disabled={savingId === item.postId}
                  variant={savedIds.has(item.postId) ? 'secondary' : 'primary'}
                  className="shrink-0 whitespace-nowrap"
                >
                  {savingId === item.postId
                    ? '保存中…'
                    : savedIds.has(item.postId)
                      ? '✓ 保存済み'
                      : '保存'}
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-400">
            iPhone は保存後「ファイル」アプリ、Android は「ダウンロード」フォルダに保存されます。
          </p>
        </>
      )}
    </div>
  );
}

/**
 * 自分の写真ダウンロード modal。地区設定 `photoDownloadMode` で ZIP / 個別を切替える。
 */
export function PhotoDownloadModal({
  open,
  onClose,
  districtId,
  uid,
  posts,
}: PhotoDownloadModalProps) {
  const mode = useDistrict()?.photoConfig?.photoDownloadMode ?? 'zip';
  const title =
    mode === 'individual' ? '📸 自分の写真をダウンロード' : '📸 自分の写真をまとめてダウンロード';
  return (
    <Modal open={open} onClose={onClose} title={title} size="md">
      {mode === 'individual' ? (
        <IndividualDownloadBody districtId={districtId} uid={uid} open={open} posts={posts ?? []} />
      ) : (
        <ZipDownloadBody districtId={districtId} uid={uid} open={open} />
      )}
    </Modal>
  );
}
